import * as React from 'react';
import ReactSelect, { components as ReactSelectComponents, } from 'react-select';
import Transition from 'react-transition-group/Transition';
import { AnalyticsTrigger } from '@core-front/analytics';
import { useIsMounted } from '@core-front/helper';
import PropsChecker from '@core-front/props-checker';
import { useTheme } from '@core-front/theme-provider';
const SelectContext = React.createContext({
    live: false,
    employee: false,
    multiple: false,
    isOpen: false,
    size: undefined,
    Check: () => null,
    Icon: () => null,
    Image: () => null,
});
const MultiValueLabel = (props) => {
    return React.createElement(React.Fragment, null, props.children);
};
const DropdownIndicator = () => {
    const { live, Icon } = React.useContext(SelectContext);
    if (live) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Icon, { modifiers: "#search", className: "select__icon-search" }),
            React.createElement(Icon, { modifiers: "#arrow-down", className: "select__icon" })));
    }
    return React.createElement(Icon, { modifiers: "#arrow-down", className: "select__icon" });
};
const Menu = (props) => {
    const { isOpen, size, employee } = React.useContext(SelectContext);
    const menuContainerBEM = (transitionState) => {
        const classArray = ['select-menu-outer'];
        if (size) {
            classArray.push('select-menu-outer--' + size);
        }
        if (employee) {
            classArray.push('select-menu-outer--employee');
        }
        if (transitionState) {
            classArray.push(transitionState);
        }
        return classArray.join(' ');
    };
    return (React.createElement(Transition, { in: isOpen, timeout: { enter: 10, exit: 100 }, mountOnEnter: true, unmountOnExit: true }, state => (React.createElement(ReactSelectComponents.Menu, Object.assign({}, props, { className: menuContainerBEM(state) }), props.children))));
};
const MenuList = (props) => {
    return React.createElement(ReactSelectComponents.MenuList, Object.assign({}, props), props.children);
};
const OptionAsterisk = (props) => {
    const { Icon } = props.components;
    return (React.createElement("div", { className: "select__asterisk-wrapper" },
        React.createElement(Icon, { modifiers: "#star", className: "select__asterisk" })));
};
const OptionIcon = (props) => {
    const { icon, components } = props;
    const { Icon } = components;
    return (React.createElement("div", { className: "select__option-icon-wrapper" },
        React.createElement(Icon, { modifiers: icon, className: "select__option-icon" })));
};
const OptionImage = (props) => {
    const { image, components } = props;
    const { Image } = components;
    return (React.createElement("div", { className: "select__image-wrapper" },
        React.createElement(Image, Object.assign({ src: image.src }, image, { className: "select__image" }))));
};
const OptionDisplayedLabel = ({ children, label }) => {
    if (children) {
        return React.createElement("div", { className: "select__option-children" }, children);
    }
    return React.createElement(React.Fragment, null, label);
};
const SubLabel = ({ subLabel }) => {
    return React.createElement("div", { className: "select__sub-label" }, subLabel);
};
const ImageOptionContent = (props) => {
    const option = props.data;
    const { Icon, Image } = React.useContext(SelectContext);
    return (React.createElement(React.Fragment, null,
        option.image && React.createElement(OptionImage, { image: option.image, components: { Image } }),
        React.createElement("div", { className: "select__image-option" },
            React.createElement(OptionDisplayedLabel, Object.assign({}, option)),
            option.showAsterisk && React.createElement(OptionAsterisk, { components: { Icon } }))));
};
const IconOptionContent = (props) => {
    const option = props.data;
    const { Icon } = React.useContext(SelectContext);
    return (React.createElement(React.Fragment, null,
        option.icon && React.createElement(OptionIcon, { icon: option.icon, components: { Icon } }),
        React.createElement("div", { className: "select__option-icon-label" },
            React.createElement(OptionDisplayedLabel, Object.assign({}, option)),
            option.showAsterisk && React.createElement(OptionAsterisk, { components: { Icon } }))));
};
const DefaultOptionContent = (props) => {
    const option = props.data;
    const { Icon } = React.useContext(SelectContext);
    return (React.createElement(React.Fragment, null,
        React.createElement(OptionDisplayedLabel, Object.assign({}, option)),
        option.showAsterisk && React.createElement(OptionAsterisk, { components: { Icon } })));
};
const OptionContent = (props) => {
    const option = props.data;
    if (option.image) {
        return React.createElement(ImageOptionContent, Object.assign({}, props));
    }
    else if (option.icon) {
        return React.createElement(IconOptionContent, Object.assign({}, props));
    }
    else {
        return React.createElement(DefaultOptionContent, Object.assign({}, props));
    }
};
const MultipleOptionContent = (props) => {
    const option = props.data;
    return (React.createElement("div", null,
        React.createElement(OptionContent, Object.assign({}, props)),
        option.subLabel && React.createElement(SubLabel, { subLabel: option.subLabel })));
};
const MultiValue = (props) => {
    const value = props.data;
    const { Icon, Image } = React.useContext(SelectContext);
    return (React.createElement(ReactSelectComponents.MultiValue, Object.assign({}, props),
        value.icon && React.createElement(OptionIcon, { icon: value.icon, components: { Icon } }),
        value.image && React.createElement(OptionImage, { image: value.image, components: { Image } }),
        props.children,
        value.showAsterisk && React.createElement(OptionAsterisk, { components: { Icon } })));
};
const Option = (props, checkProps) => {
    const { employee, multiple, Check } = React.useContext(SelectContext);
    const option = props.data;
    const employeeOption = option.employee && !employee;
    const optionBEM = () => {
        if (employeeOption) {
            return 'select__option--employee';
        }
    };
    if (multiple) {
        return (React.createElement(ReactSelectComponents.Option, Object.assign({}, props, { className: optionBEM() }),
            React.createElement(Check, Object.assign({}, checkProps, { onChange: () => {
                    var _a, _b;
                    // @ts-expect-error Handler expects a mouse event instance, but I got nothing to give it from an onChange handler. Does not seem to error, so should be fine for now.
                    (_b = (_a = props.innerProps).onClick) === null || _b === void 0 ? void 0 : _b.call(_a);
                }, checked: props.isSelected, disabled: props.isDisabled, employee: employeeOption, employeeBackground: props.data.employee && false }),
                React.createElement(MultipleOptionContent, Object.assign({}, props)))));
    }
    return (React.createElement(ReactSelectComponents.Option, Object.assign({}, props, { className: optionBEM() }),
        React.createElement(OptionContent, Object.assign({}, props)),
        option.subLabel && React.createElement(SubLabel, { subLabel: option.subLabel })));
};
const SingleValue = (props) => {
    const value = props.data;
    const { Icon, Image } = React.useContext(SelectContext);
    return (React.createElement(ReactSelectComponents.SingleValue, Object.assign({}, props),
        value.icon && React.createElement(OptionIcon, { icon: value.icon, components: { Icon } }),
        value.image && React.createElement(OptionImage, { image: value.image, components: { Image } }),
        React.createElement("div", { className: "select__text-wrapper" }, value.children || value.label),
        value.showAsterisk && React.createElement(OptionAsterisk, { components: { Icon } })));
};
const ClearIndicator = () => null;
const IndicatorSeparator = () => null;
const MultiValueRemove = () => null;
const SelectCore = (props) => {
    return (React.createElement(PropsChecker, { componentName: "Select", componentProps: props, defaultValueName: "defaultValue", valueName: "value", onChangeNames: "onChange", isComponentDisabled: props.disabled },
        React.createElement(AnalyticsTrigger, { trigger: [
                {
                    callback: 'onShow',
                    id: `Select - Open - ${props.id}`,
                    elementId: `${props.id}`,
                    elementType: 'select',
                    interactionType: 'open',
                },
                {
                    callback: 'onHide',
                    id: `Select - Close - ${props.id}`,
                    elementId: `${props.id}`,
                    elementType: 'select',
                    interactionType: 'close',
                },
                {
                    callback: 'onChange',
                    id: `Select - Choose - ${props.id}`,
                    elementId: `${props.id}`,
                    elementType: 'select',
                    interactionType: 'select',
                },
            ] },
            React.createElement(SelectCoreInner, Object.assign({}, props)))));
};
const SelectCoreInner = (props) => {
    const { className, components: { Check, FormHelper, Icon, Image }, defaultValue, disabled, elementRef, employee, helper, hideLabel, id, isInvalid, label, live, multiple, name, noResultsText, onChange, onHide, onShow, options, placeholder, portalTarget, simpleValue, size, style, value, required, } = props;
    const isValueArray = (value) => {
        return Array.isArray(value);
    };
    const [isOpen, setIsOpen] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const isMounted = useIsMounted();
    const getValue = () => {
        return typeof value !== 'undefined' ? findValueFromOptions(value, options) : values;
    };
    const findSimpleValueFromOptions = (value, options) => {
        if (Array.isArray(value)) {
            return options.filter(object => value.find(option => option === object.value));
        }
        else {
            return options.find(option => option.value === value) || null;
        }
    };
    const findDefaultValueFromOptions = (value, options) => {
        if (!value) {
            return null;
        }
        if (isValueArray(value)) {
            return options.filter(object => value.find(option => option.value === object.value));
        }
        else {
            return options.find(option => option.value === value.value) || null;
        }
    };
    const findValueFromOptions = (value, options) => {
        if ((simpleValue && typeof value === 'string') || (Array.isArray(value) && typeof value[0] === 'string')) {
            return findSimpleValueFromOptions(value, options);
        }
        else {
            return findDefaultValueFromOptions(value, options);
        }
    };
    const [values, setValues] = React.useState(findValueFromOptions(defaultValue, options));
    const nativeSelectValue = () => {
        if (!values) {
            return undefined;
        }
        if (multiple) {
            if (Array.isArray(values)) {
                values.map(values => values.value);
            }
            else {
                return [values.value];
            }
        }
        if (Array.isArray(values)) {
            values.map(values => values.value);
        }
        return values.value;
    };
    const getSimpleValue = (value) => {
        if (!value) {
            return null;
        }
        if (isValueArray(value)) {
            return value.map(value => value.value);
        }
        else {
            return value.value;
        }
    };
    const handleChange = (values) => {
        if (typeof value === 'undefined') {
            setValues(values);
        }
        if (onChange && !simpleValue) {
            onChange(values);
        }
        if (onChange && simpleValue) {
            onChange(getSimpleValue(values));
        }
    };
    const getContent = () => {
        if (!props.contentRenderer) {
            return null;
        }
        return props.contentRenderer();
    };
    const BEM = () => {
        const classArray = ['select'];
        if (disabled) {
            classArray.push('is-disabled');
        }
        if (isInvalid) {
            classArray.push('is-invalid');
        }
        if (isOpen) {
            classArray.push('is-open');
        }
        if (isFocused) {
            classArray.push('is-focused');
        }
        if (size) {
            classArray.push(`select--${size}`);
        }
        if (style) {
            classArray.push(`select--${style}`);
        }
        if (employee) {
            classArray.push('select--employee');
        }
        if (live) {
            classArray.push('select--searchable');
        }
        if (hideLabel) {
            classArray.push(`select--no-label-${hideLabel}`);
        }
        if (className) {
            classArray.push(className);
        }
        return classArray.join(' ');
    };
    const onMenuOpen = () => {
        if (!isOpen) {
            setIsOpen(true);
        }
        if (onShow) {
            onShow();
        }
    };
    const onMenuClose = () => {
        if (isOpen) {
            setIsOpen(false);
            if (onHide) {
                onHide();
            }
        }
    };
    const onFocus = () => {
        if (isFocused) {
            return;
        }
        setIsFocused(true);
    };
    const onBlur = () => {
        if (!isFocused) {
            return;
        }
        setIsFocused(false);
    };
    const getNoResultsText = () => {
        if (!noResultsText) {
            return null;
        }
        return noResultsText;
    };
    const { portalRoot } = useTheme();
    const renderReactSelect = () => {
        const customComponents = {
            ClearIndicator,
            DropdownIndicator,
            IndicatorSeparator,
            Menu,
            MenuList,
            MultiValue,
            MultiValueLabel,
            MultiValueRemove,
            Option,
            SingleValue,
        };
        const styles = {
            container: () => ({}),
            control: () => ({}),
            indicatorsContainer: () => ({}),
            input: () => ({}),
            menu: () => ({}),
            menuList: () => ({}),
            menuPortal: base => (Object.assign(Object.assign({}, base), { zIndex: 31 })),
            multiValue: () => ({}),
            multiValueLabel: () => ({}),
            multiValueRemove: () => ({}),
            noOptionsMessage: () => ({}),
            option: () => ({}),
            placeholder: () => ({}),
            singleValue: () => ({}),
            valueContainer: () => ({}),
        };
        const selectContextValue = {
            live,
            employee,
            multiple,
            isOpen,
            size,
            Check,
            Icon,
            Image,
        };
        return (React.createElement(SelectContext.Provider, { value: selectContextValue },
            React.createElement(ReactSelect, { id: id, instanceId: id, "aria-labelledby": `${id}-label`, className: "select__wrapper", name: name, options: options, value: getValue(), onChange: handleChange, classNamePrefix: "select", components: customComponents, onMenuOpen: onMenuOpen, onMenuClose: onMenuClose, onFocus: onFocus, onBlur: onBlur, menuIsOpen: isOpen, styles: styles, isMulti: multiple, closeMenuOnSelect: !multiple, hideSelectedOptions: false, isDisabled: disabled, placeholder: placeholder ? placeholder : '', menuPortalTarget: typeof portalTarget !== 'undefined' ? portalTarget : portalRoot || (typeof window !== 'undefined' ? document.body : undefined), isClearable: live, noOptionsMessage: getNoResultsText, backspaceRemovesValue: true, menuShouldScrollIntoView: true, isSearchable: live, required: required })));
    };
    const renderNativeSelect = () => {
        return (React.createElement("div", { className: "select__wrapper" },
            React.createElement("div", { className: "select__control" },
                React.createElement("div", { className: "select__value-container" },
                    React.createElement("select", { id: id, name: name, className: "select__native-select", disabled: disabled, multiple: multiple, defaultValue: nativeSelectValue() }, options.map((option, index) => {
                        return (React.createElement("option", { key: index, value: option.value }, option.label));
                    }))))));
    };
    return (React.createElement("div", { className: BEM(), ref: elementRef, 
        // Prevent click events from propagating to parents
        onClick: e => e.stopPropagation() },
        React.createElement("div", { className: "select__inner" },
            React.createElement("label", { className: "select__label", id: `${id}-label`, htmlFor: id, "aria-hidden": isMounted }, label),
            isMounted ? renderReactSelect() : renderNativeSelect(),
            getContent()),
        helper && React.createElement(FormHelper, Object.assign({}, helper, { className: "select__form-helper" }))));
};
export { SelectCore as default, SelectCoreInner };
